import axios from 'axios';
import * as env from '@/config/env';
import Cookies from 'js-cookie';
import Store from "@/store/index";
import { useCustomComposable } from '@/composable';
import * as offline from '@/offline';
import { dropWorkerRuntimeCaches } from '@/serviceWorker/registration';
import { forgetLocalePrefs } from '@/views/Settings/Language/localePrefs';
import { installBusyHandling } from './busy';
const { logOut } = useAuth();
export const SESSION_EXPIRED_KEY = "ah.sessionExpired";
const apiHost = env.API_URI;
export const axiosInstance = axios.create({ baseURL: apiHost });
export const axiosInstanceWithFormData = axios.create({ baseURL: apiHost });
export const axiosInstanceWithoutCompany = axios.create({ baseURL: apiHost });
export const axiosInstanceWithoutCompanyWithFormData = axios.create({ baseURL: apiHost });
export const axiosInstanceWithoutSecure = axios.create({ baseURL: apiHost });
export const axiosInstanceWithoutSecureWithFormData = axios.create({ baseURL: apiHost });

[
    axiosInstance,
    axiosInstanceWithFormData,
    axiosInstanceWithoutCompany,
    axiosInstanceWithoutCompanyWithFormData,
    axiosInstanceWithoutSecure,
    axiosInstanceWithoutSecureWithFormData
].forEach(installBusyHandling);



axiosInstance.interceptors.request.use((req) => {
    // Unreadable under httpOnly cookies, so no header is sent and the server reads the cookie instead.
    const token = Cookies.get('accessToken') || '';
    const companyId = localStorage.getItem('selectedCompany') || "";
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
        'companyId': companyId
    }
    req.headers = headers;
    return req;
}, error => {
    return Promise.reject(error);
});

axiosInstanceWithFormData.interceptors.request.use((req) => {
    const token = Cookies.get('accessToken') || '';
    const companyId = localStorage.getItem('selectedCompany') || "";
    const headers = {
        'Content-Type': 'multipart/form-data',
        ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
        'companyId': companyId
    }
    req.headers = headers;
    return req;
}, error => {
    return Promise.reject(error);
});

axiosInstanceWithoutCompany.interceptors.request.use((req) => {
    const token = Cookies.get('accessToken') || '';
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': 'Bearer ' + token } : {})
    }
    req.headers = headers;
    return req;
}, error => {
    return Promise.reject(error);
});

axiosInstanceWithoutCompanyWithFormData.interceptors.request.use((req) => {
    const token = Cookies.get('accessToken') || '';
    const headers = {
        'Content-Type': 'multipart/form-data',
        ...(token ? { 'Authorization': 'Bearer ' + token } : {})
    }
    req.headers = headers;
    return req;
}, error => {
    return Promise.reject(error);
});

axiosInstanceWithoutSecure.interceptors.request.use((req) => {
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }
    req.headers = headers;
    return req;
}, error => {
    return Promise.reject(error);
});

axiosInstanceWithoutSecureWithFormData.interceptors.request.use((req) => {
    const headers = {
        'Content-Type': 'multipart/form-data'
    }
    req.headers = headers;
    return req;
}, error => {
    return Promise.reject(error);
});




let renewal = null;
let renewalAfter = null;

const requestAuth = (id, retried = false) => new Promise((resolve, reject) => {
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };
    const readable = Cookies.get('refreshToken') || '';
    const sentHeader = Boolean(readable);
    if (sentHeader) headers['refresh-token'] = readable;
    axios.post(apiHost + env.GENERATETOKEN_V2, { uid: id }, { headers }).then((result) => {
        resolve(result.data);
    }).catch((error) => {
        const data = error?.response?.data;
        // Another tab exchanged this token first; the server's reuse grace covers the race.
        if (data?.isRotated && !retried) {
            requestAuth(id, true).then(resolve, reject);
            return;
        }
        // Nothing to refresh with and the server has nothing to read: the session is gone.
        if (!sentHeader && error?.response?.status === 400) {
            logOut({ expired: true });
        }
        console.error('error', data);
        reject(data);
        if (data?.isLogout) {
            logOut({ expired: true });
        }
    });
});

const beginRenewal = (id) => {
    renewal = requestAuth(id).finally(() => { renewal = null; });
    return renewal;
};

/* A request refused because its token ran out needs any newer token: the renewal already on its way will do. */
const renewExpired = (id) => renewal || beginRenewal(id);

/* A page asks for a renewal after something a session carries has changed (a workspace joined or made). A renewal
 * already on its way was issued without it, so the page gets one that begins once that has landed, shared by
 * everyone who asked in the meantime. */
const renewFromNow = (id) => {
    if (!renewal) return beginRenewal(id);
    if (!renewalAfter) {
        renewalAfter = renewal.catch(() => null).then(() => {
            renewalAfter = null;
            return renewExpired(id);
        });
    }
    return renewalAfter;
};

export const getAuth = async (id, isFirst) => {
    const data = await renewFromNow(id);
    if (isFirst) {
        // Clears the value earlier builds stored under this key.
        localStorage.removeItem('updateToken');
    }
    return data;
};
const abortControllers={};

function initializeAbortController(options, abortControllers) {
    const abortKey = makeUniqueId(7);
    let signal;
    if (options?.signal) {
        abortControllers[abortKey] = options.signal;
        signal = options.signal;
    } else {
        abortControllers[abortKey] = new AbortController();
        signal = abortControllers[abortKey].signal;
    }
    return { signal, abortKey };
}

export function abortAllRequests() {
    for (const key in abortControllers) {
        try {
            if (abortControllers[key]) {
                abortControllers[key].abort();
                delete abortControllers[key];
            }
        } catch (error) {
            console.error("Error in aborting request", error);
        }
    }
}

const {makeUniqueId} = useCustomComposable();
export const apiRequest = (type, endPoint, data, dataType, options) => {
    return new Promise((resolve, reject) => {
        try {
            if (type === 'post' || type === 'patch' || type === 'get' || type === 'put' || type === 'delete') {
                const { signal, abortKey } = initializeAbortController(options, abortControllers);

                let rData;
                if (dataType === 'form') {
                    rData = axiosInstanceWithFormData[type](endPoint, data, {signal, ...options});
                } else {
                    if (type === 'get') {
                        rData = axiosInstance[type](endPoint, {signal, ...options});
                    } else {
                        rData = axiosInstance[type](endPoint, data, {signal, ...options});
                    }
                }

                rData
                    .then((resData) => {
                        resolve(resData);
                        delete abortControllers[abortKey];
                        offline.maybeCacheResponse(type, endPoint, resData);
                    })
                    .catch(async (err) => {
                        delete abortControllers[abortKey];
                        if(err.code === 'ERR_CANCELED'){
                            return;
                        }
                        if (err?.response?.data?.isLogout) {
                            logOut({ expired: true });
                        }else if (err?.response?.data?.isJwtError) {
                            const userId = localStorage.getItem('userId') || "";
                            await renewExpired(userId);
                            apiRequest(type, endPoint, data, dataType, options).then((sData)=>{
                                resolve(sData);
                            }).catch((err) => {
                                reject(err);
                            });
                            return;
                        }
                        const offlineResult = await offline.handleOfflineFailure(type, endPoint, data, dataType, err);
                        if (offlineResult) { resolve(offlineResult); return; }
                        reject(err);
                    });
            } else {
                console.info(type)
            }
        } catch (error) {
            console.error('error', error);
            reject(error);
        }
    })
}

// SEC-06 — let the offline write-queue replay through the real request path.
offline.registerReplayer(apiRequest);


export const apiRequestWithoutCompnay = (type, endPoint, data, dataType,options) => {
    return new Promise((resolve, reject) => {
        try {
            if (type === 'post' || type === 'patch' || type === 'get' || type === 'delete' || type === 'put') {
                const { signal, abortKey } = initializeAbortController(options, abortControllers);
                let rData;
                if (dataType === 'form') {
                    rData = axiosInstanceWithoutCompanyWithFormData[type](endPoint, data);
                } else {
                    if (type === 'get') {
                        rData = axiosInstanceWithoutCompany[type](endPoint, {signal, ...options});
                    } else {
                        rData = axiosInstanceWithoutCompany[type](endPoint, data,{signal, ...options});
                    }
                }
                rData
                    .then((resData) => {
                        delete abortControllers[abortKey];
                        resolve(resData);
                    })
                    .catch(async (err) => {
                        delete abortControllers[abortKey];
                        if(err.code === 'ERR_CANCELED'){
                            return;
                        }
                    if (err?.response?.data?.isLogout) {
                        logOut({ expired: true });
                    } else if (err?.response?.data?.isJwtError) {
                        const userId = localStorage.getItem('userId') || "";
                        await renewExpired(userId);
                        apiRequestWithoutCompnay(type, endPoint, data, dataType, options).then((sData)=>{
                            resolve(sData);
                        }).catch((err) => {
                            reject(err);
                        });
                        return;
                    }
                    reject(err);
                });
            } else {
                console.info(type)
            }
        } catch (error) {
            console.error('error', error);
            reject(error);
        }
    })
}

export const apiRequestWithoutSecure = (type, endPoint, data, dataType) => {
    return new Promise((resolve, reject) => {
        try {
            if (type === 'post' || type === 'patch' || type === 'get' || type === 'delete' || type === 'put') {
                let rData;
                if (dataType === 'form') {
                    rData = axiosInstanceWithoutSecureWithFormData[type](endPoint, data);
                } else {
                    rData = axiosInstanceWithoutSecure[type](endPoint, data);
                }
                rData
                    .then((resData) => {
                        resolve(resData);
                    })
                    .catch(async (err) => {
                        reject(err);
                    });
            } else {
                console.info(type)
                reject("API TYPE NOT FOUND");
            }
        } catch (error) {
            console.error('error', error);
            reject(error);
        }
    })
}

export function useAuth() {
    const getters = Store.getters;


    const removeLocalValue = (value) => {
        localStorage.removeItem("isLogging");
        localStorage.removeItem("currentUserEmail");
        localStorage.removeItem("SubmenuScreen");
        localStorage.removeItem("selectedCompany");
        localStorage.removeItem("SubmenuScreen");
        localStorage.removeItem("currentLoggedInUserDetails");
        localStorage.removeItem("userId");
        localStorage.removeItem("webTokens");
        localStorage.removeItem("updateToken");
        localStorage.removeItem('logged');
        forgetLocalePrefs();
        try { offline.clearOffline(); } catch (e) { /* offline cleanup best-effort */ }
        dropWorkerRuntimeCaches();
        if(value?.withOutRefresh !== true){
            window.location.reload();
        }
    };

    async function logOut(data) {
        if (data?.expired) {
            try { sessionStorage.setItem(SESSION_EXPIRED_KEY, "1"); } catch (e) { /* the login page then just shows no notice */ }
        }
        try {
            const userId = localStorage.getItem('userId') || '';
            
            const cleanup = async (value) => {
                const socket = getters['settings/getSocketInstance'];
                if(socket?.id){
                    socket.emit('disconnectNameSpace', socket.id);
                }
                removeLocalValue(value);
            };
    
            if (userId && data?.islogOut === true) {
                try {
                    await apiRequestWithoutCompnay("post", env.LOGOUT, { id: userId });
                } catch (error) {
                    console.error('Error in logout', error);
                    cleanup(data);
                }
            }            
            await cleanup(data);
        } catch (error) {
            console.error("ERROR in logout", error);
            removeLocalValue();
        }
    }

    return { logOut };
}